import React from 'react';

// Card component for dashboard metrics
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  change?: {
    value: string;
    positive: boolean;
  };
  bgColor?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  icon, 
  change,
  bgColor = 'bg-white'
}) => (
  <div className={`${bgColor} rounded-lg shadow p-5 flex items-center`}>
    <div className="mr-4 bg-gray-100 p-3 rounded-full text-2xl">
      {icon}
    </div>
    <div>
      <div className="text-sm text-gray-500">{title}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {change && (
        <div className={`text-xs flex items-center mt-1 ${
          change.positive ? 'text-green-600' : 'text-red-600'
        }`}>
          <span>{change.positive ? '↑' : '↓'}</span>
          <span className="ml-1">{change.value} from last month</span>
        </div>
      )}
    </div>
  </div>
);

// Status indicator component
interface StatusIndicatorProps {
  label: string;
  status: 'healthy' | 'warning' | 'critical';
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ label, status }) => {
  const statusClasses = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    critical: 'bg-red-500'
  };
  
  return (
    <div className="flex items-center">
      <div className={`h-3 w-3 rounded-full mr-2 ${statusClasses[status]}`}></div>
      <span className="text-sm">{label}</span>
    </div>
  );
};

// Quick action button component
interface QuickActionProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  bgColor?: string;
}

const QuickAction: React.FC<QuickActionProps> = ({ 
  label, 
  icon, 
  onClick,
  bgColor = 'bg-[#3A5E63]'
}) => (
  <button 
    onClick={onClick}
    className={`${bgColor} text-white p-3 rounded-lg flex items-center justify-center flex-col`}
  >
    <span className="text-2xl mb-1">{icon}</span>
    <span className="text-xs">{label}</span>
  </button>
);

// Notification component
interface NotificationProps {
  title: string;
  time: string;
  message: string;
  type: 'info' | 'warning' | 'success';
}

const Notification: React.FC<NotificationProps> = ({ title, time, message, type }) => {
  const typeClasses = {
    info: 'bg-blue-100 border-blue-500',
    warning: 'bg-yellow-100 border-yellow-500',
    success: 'bg-green-100 border-green-500'
  };
  
  return (
    <div className={`${typeClasses[type]} border-l-4 p-4 mb-3 rounded-r`}>
      <div className="flex justify-between">
        <h3 className="font-medium">{title}</h3>
        <span className="text-xs text-gray-500">{time}</span>
      </div>
      <p className="text-sm mt-1">{message}</p>
    </div>
  );
};

const Dashboard: React.FC = () => {
  // Mock functions for demonstration
  const handleAddUser = () => {
    alert('Add user clicked');
  };
  
  const handleAddContent = () => {
    alert('Add content clicked');
  };
  
  const handleSchedule = () => {
    alert('Schedule appointment clicked');
  };
  
  const handleSettings = () => {
    alert('Settings clicked');
  };
  
  return (
    <div className="space-y-6 pt-2">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-[#344854] py-2">Dashboard Overview</h1>
        <div className="text-sm text-gray-500">Last updated: Today, 2:30 PM</div>
      </div>
      
      {/* Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard 
          title="Total Users" 
          value="1,247" 
          icon="👥"
          change={{ value: "12%", positive: true }}
        />
        <MetricCard 
          title="Active Memberships" 
          value="834" 
          icon="💳"
          change={{ value: "5%", positive: true }}
        />
        <MetricCard 
          title="Appointments Today" 
          value="28" 
          icon="📅"
          change={{ value: "3%", positive: false }}
        />
        <MetricCard 
          title="Revenue (MTD)" 
          value="$24,567" 
          icon="💰"
          change={{ value: "8%", positive: true }}
        />
      </div>
      
      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-5">
        <h2 className="text-lg font-medium mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction label="Add User" icon="👤" onClick={handleAddUser} />
          <QuickAction 
            label="Add Content" 
            icon="📝" 
            onClick={handleAddContent} 
            bgColor="bg-[#5B8A72]"
          />
          <QuickAction 
            label="Schedule" 
            icon="🗓️" 
            onClick={handleSchedule} 
            bgColor="bg-[#6B7D8E]"
          />
          <QuickAction 
            label="Settings" 
            icon="⚙️" 
            onClick={handleSettings} 
            bgColor="bg-[#86A3A3]"
          />
        </div>
      </div>
      
      {/* Two column layout for status and notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Status */}
        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-lg font-medium mb-4">System Status</h2>
          <div className="space-y-3">
            <StatusIndicator label="Website" status="healthy" />
            <StatusIndicator label="Booking System" status="healthy" />
            <StatusIndicator label="Payment Processing" status="warning" />
            <StatusIndicator label="Email Delivery" status="healthy" />
            <StatusIndicator label="Database Backups" status="healthy" />
          </div>
        </div>
        
        {/* Notifications */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Recent Notifications</h2>
            <button className="text-sm text-[#3A5E63]">View All</button>
          </div>
          <div className="space-y-3">
            <Notification 
              title="Payment System Alert" 
              time="Today, 1:23 PM" 
              message="The payment gateway is experiencing some delays. Monitoring in progress." 
              type="warning"
            />
            <Notification 
              title="New User Registration" 
              time="Today, 11:05 AM" 
              message="5 new users registered in the last hour." 
              type="info"
            />
            <Notification 
              title="Backup Completed" 
              time="Today, 3:30 AM" 
              message="Daily database backup completed successfully." 
              type="success"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
